import {
  ActionIcon,
  AspectRatio,
  Button,
  Chip,
  Group,
  Modal,
  ModalProps,
  SimpleGrid,
  Space,
  Stack,
  Tabs,
  Text,
  Textarea,
  useMantineTheme,
} from '@mantine/core';
import { ViewpointType } from '@models/ropolo';
import { createFeedbacks, getViewpointsList } from '@services/rating';
import { IconThumbDown, IconThumbUp } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import storageUtil from '@utils/storageUtil';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { HomePageProps } from '../home/types';

type Props = { home: HomePageProps; modal: ModalProps };

// eslint-disable-next-line no-shadow
enum GeneralRate {
  Positive = 'positive',
  Negative = 'negative',
}

const RatingComponent = (props: Props) => {
  const router = useRouter();
  const { ratingOrderId } = router.query;

  const { home, modal } = props;
  const { vendor } = home;

  const theme = useMantineTheme();
  const [general, setGeneral] = useState<GeneralRate | undefined>(undefined);
  const [description, setDescription] = useState<string>('');
  const [viewpointsId, setViewpointsId] = useState<string[]>([]);

  const { data: viewPointData } = useQuery({
    queryKey: ['getViewpointsList'],
    queryFn: () => getViewpointsList(),
  });

  const { isLoading, mutate } = useMutation({
    mutationFn: createFeedbacks,
    onSuccess: modal.onClose,
  });

  let satisfaction = 0;
  switch (general) {
    case GeneralRate.Positive:
      satisfaction = 5;
      break;

    case GeneralRate.Negative:
      satisfaction = 1;
      break;

    default:
      satisfaction = 1;
      break;
  }

  const logoUrl =
    vendor?.subDomain && vendor?.logo
      ? storageUtil.getItemImage(vendor.subDomain, vendor.logo)
      : storageUtil.getCoverImage('ss-icon.svg');

  const header = (
    <>
      <AspectRatio miw={84} ratio={1 / 1} mx="auto">
        <Image
          alt={vendor?.name || ''}
          width={84}
          height={84}
          src={logoUrl}
          style={{ borderRadius: theme.radius.lg }}
        />
      </AspectRatio>

      <Stack spacing="xs">
        <Text align="center">امتیاز به رستوران</Text>
        <Text align="center" weight="bold">
          {vendor?.name}
        </Text>

        <Text align="center" size="sm">
          لطفا به کیفیت پذیرایی امتیاز دهید
        </Text>
      </Stack>

      <Group align="center" position="center">
        {!general || general === GeneralRate.Negative ? (
          <ActionIcon
            id="feedback_like"
            size="2.8rem"
            variant="transparent"
            onClick={() => setGeneral(GeneralRate.Negative)}
          >
            <IconThumbDown size="2.8rem" color="#FF3939" style={{ filter: 'drop-shadow(0px 0px 2px #FF3939)' }} />
          </ActionIcon>
        ) : null}

        {!general || general === GeneralRate.Positive ? (
          <ActionIcon
            id="feedback_dislike"
            size="2.8rem"
            variant="transparent"
            onClick={() => setGeneral(GeneralRate.Positive)}
          >
            <IconThumbUp size="2.8rem" color="#06DF70" style={{ filter: 'drop-shadow(0px 0px 2px #06DF70)' }} />
          </ActionIcon>
        ) : null}
      </Group>
    </>
  );
  const step1 = <Space mt="lg" />;

  const step2 = (
    <>
      <Chip.Group value={viewpointsId} onChange={setViewpointsId} multiple>
        <Tabs defaultValue={general}>
          <Stack>
            <Tabs.List grow position="center">
              <Tabs.Tab value="positive">نکات مثبت</Tabs.Tab>
              <Tabs.Tab value="negative">نکات منفی</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="positive">
              <SimpleGrid cols={2} verticalSpacing="sm" spacing="xs">
                {viewPointData?.data
                  ?.filter((pV) => pV.type === ViewpointType.PROS)
                  .map((pV) => (
                    <Chip
                      key={pV.content}
                      value={pV.id?.toString()}
                      size="lg"
                      radius="sm"
                      styles={{
                        label: {
                          width: '98%',
                          fontSize: theme.fontSizes.xs,
                        },
                      }}
                    >
                      {pV.content}
                    </Chip>
                  ))}
              </SimpleGrid>
            </Tabs.Panel>
            <Tabs.Panel value="negative">
              <SimpleGrid cols={2} verticalSpacing="sm" spacing="xs">
                {viewPointData?.data
                  ?.filter((pV) => pV.type === ViewpointType.CONS)
                  .map((pV) => (
                    <Chip
                      key={pV.content}
                      value={pV.id?.toString()}
                      size="lg"
                      radius="sm"
                      styles={{
                        label: {
                          width: '98%',
                          fontSize: theme.fontSizes.xs,
                        },
                      }}
                    >
                      {pV.content}
                    </Chip>
                  ))}
              </SimpleGrid>
            </Tabs.Panel>
          </Stack>
        </Tabs>
      </Chip.Group>

      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="جزئیات نظر خود را در مورد این رستوران بنویسید"
      />

      <Button
        loading={isLoading}
        onClick={() => {
          mutate({
            description,
            satisfaction,
            orderId: (ratingOrderId as string) || '',
            viewpointsId: viewpointsId.map((vP) => Number(vP)),
          });
        }}
      >
        ثبت
      </Button>
    </>
  );

  return (
    <Modal onClose={modal.onClose} fullScreen={modal.fullScreen} opened={modal.opened} centered={modal.centered}>
      <Stack>
        {header}
        {general ? step2 : step1}
      </Stack>
    </Modal>
  );
};

export default RatingComponent;
